<script setup lang="ts">
import { useKeycloak } from '@nxtpeople/nxt-vue-keycloak';
import { NxtSidebar, NxtSidebarNavigationItem, vPurifyHtml } from '@nxtpeople/nxt-styles';
import { Scope } from '@nxtpeople/nxt-vue-library';
import { SYSTEM_NOTIFICATIONS_ENDPOINTS } from './core/collections/system-notifications/system-notification.endpoints';
import { SystemNotification } from './core/collections/system-notifications/system-notification.interface';
import AppModals from './core/components/layout-elements/AppModals.vue';
import PageHeader from './core/components/layout-elements/PageHeader.vue';
import Settings from './core/components/settings/Settings.vue';
import { ScopeType } from './core/models/allowed-scopes.interface';

//#region Globals
const { t } = useI18n();
const { state } = useStore();
const { handleUserChange, handleLocaleChange } = useApp();
const { isAuthenticated } = useKeycloak();
//#endregion

//#region locale
const { locale } = useI18n();
watch(locale, handleLocaleChange, { immediate: true });
//#endregion

//#region User
const userInfo = computed(() => state.userInfo?.userInfo);
watch(userInfo, handleUserChange, { immediate: true });
//#endregion

//#region Panels
const showNotificationPanel = ref(false);
const showSettingsPanel = ref(false);
//#endregion

//#region App Status
const { data: systemNotification, execute } = useFetch<SystemNotification>(SYSTEM_NOTIFICATIONS_ENDPOINTS.GET(), { immediate: false });

watch(isAuthenticated, (isAuthenticated) => {
   if (isAuthenticated) execute();
});
//#endregion

//#region Navigation
const navigationItems = computed<Array<NxtSidebarNavigationItem>>(() => [
   {
      to: '/users',
      icon: 'fas fa-user-group',
      label: t('user.user', 2),
      hide: !checkAccess(ScopeType.Users, [Scope.View]),
   },
   {
      to: '/teams',
      icon: 'fas fa-users',
      label: t('team.team', 1),
      hide: !checkAccess(ScopeType.Teams, [Scope.View]),
   },
   {
      to: '/employers',
      icon: 'fas fa-briefcase',
      label: t('employer.employer', 2),
      hide: !checkAccess(ScopeType.Employers, [Scope.View]),
   },
   {
      to: '/clusters',
      icon: 'fas fa-users-class',
      label: t('cluster.cluster', 2),
      hide: !checkAccess(ScopeType.Clusters, [Scope.View]),
   },
   {
      to: '/internal-job-functions',
      icon: 'fa-brands fa-black-tie',
      label: t('internal-job-function.internal-job-function', 2),
      hide: !checkAccess(ScopeType.InternalJobFunctions, [Scope.View]),
   },
   {
      to: '/job-functions',
      icon: 'fas fa-id-card-clip',
      label: t('generic.job-functions', 2),
      hide: !checkAccess(ScopeType.JobFunctions, [Scope.View, Scope.Update, Scope.Create, Scope.Delete]),
   },
   {
      to: '/tenants',
      icon: 'fas fa-building',
      label: t('tenant.tenant', 2),
      hide: !checkAccess(ScopeType.Tenants, [Scope.View, Scope.Update]),
   },
   {
      to: `/tenants/${userInfo.value?.tenant_id}`,
      icon: 'fas fa-building',
      label: t('tenant.tenant', 1),
      hide: !checkAccess(ScopeType.Tenants, [Scope.View]) || checkAccess(ScopeType.Tenants, [Scope.View, Scope.Update]),
   },
   {
      to: '/companies',
      icon: 'fas fa-buildings',
      label: t('companies.companies', 2),
      hide: !checkAccess(ScopeType.Tenants, [Scope.View, Scope.Update]),
   },
]);
//#endregion
</script>

<template>
   <NxtMainLayout class="app" :http="http" :user-info="userInfo">
      <template #nav>
         <NxtSidebar :loading="!userInfo" :navigation-items :shared-routes="[]" />
      </template>

      <template #status-banner>
         <NxtNotificationBanner v-if="systemNotification?.id">
            <span v-purify-html="systemNotification.message[$i18n.locale] || systemNotification.message.en"></span>
         </NxtNotificationBanner>
      </template>

      <template #header>
         <PageHeader @on-show-notification-panel="showNotificationPanel = true" @on-show-settings-panel="showSettingsPanel = true" />
      </template>

      <!-- Settings Panel -->
      <Transition name="fade">
         <Settings v-if="showSettingsPanel" @on-close="showSettingsPanel = false" />
      </Transition>

      <!-- Global Modals -->
      <AppModals />

      <RouterView v-slot="{ Component }">
         <transition name="fade" mode="out-in">
            <Component :is="Component" />
         </transition>
      </RouterView>
   </NxtMainLayout>
</template>

<style lang="scss" scoped>
.app:deep(.nxt-tab-container__tab) {
   border-bottom-left-radius: var(--nxt-radius);
   border-bottom-right-radius: var(--nxt-radius);
   padding: var(--nxt-gutter);
}

.app:deep(.nxt-tab-container__nav__wrapper) {
   background: linear-gradient(to top, var(--nxt-grey), var(--nxt-grey) toRem(0.5), var(--nxt-white) toRem(0.5));
}
</style>
